import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import { proprietaMisurataIcons } from "@/utils/proprietaMisurataIcons";
import {
  isDanger,
  isOld,
  proprietaMisurataFormat,
  proprietaMisurataGetPercent,
} from "@/utils/proprietaMisurataUtils";
import { Card, Icon, SvgIcon, Tooltip } from "@mui/material";
import { ComponentProps, useMemo } from "react";
import { useTranslation } from "react-i18next";
import typography from "@/theme/base/typography";
import moment from "moment";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale,
  ArcElement,
} from "chart.js";
import Button from "@/elements/Button";
import {
  NodoDto,
  ProprietaMisurataDto,
  ProprietaMisurataValueDto,
  useGetRegistriDispositivo,
} from "@/api";
import { getMascheraDispositivoHelper } from "../DispositivoDetails/maschere";
import numeral from "numeral";

ChartJS.register(
  LineController,
  LineElement,
  ArcElement,
  PointElement,
  LinearScale,
  Title,
  CategoryScale
);

function ProprietaMisurataLabel({
  dispositivo,
  proprietaMisurata,
  value,
  typographyVariantPrimary = "h3",
  typographyVariantSecondary = "body2",
  alignItems = "center",
  skipMeasureUnit = false,
}: {
  dispositivo: NodoDto;
  proprietaMisurata: ProprietaMisurataDto;
  value: ProprietaMisurataValueDto | null;
  typographyVariantPrimary?: ComponentProps<typeof Typography>["variant"];
  typographyVariantSecondary?: ComponentProps<typeof Typography>["variant"];
  alignItems?: "center" | "flex-start";
  skipMeasureUnit?: boolean;
}) {
  const { t } = useTranslation();
  const { size } = typography;

  const type = proprietaMisurata.type;
  const Icon_ = proprietaMisurataIcons[type.icona];

  const valueText = useMemo(() => {
    if (!type || typeof value?.value !== "number") {
      return "-";
    }

    if (
      proprietaMisurata.type.influxDbTag == "trendTemp" ||
      proprietaMisurata.type.influxDbTag === "rms_trend"
    ) {
      switch (value.value) {
        case 0:
          return t("proprietaMisurataLabel.trend.stabile");
        case 1:
          return t("proprietaMisurataLabel.trend.aumento");
        case 2:
          return t("proprietaMisurataLabel.trend.diminuzione");
        default:
          return "-";
      }
    } else {
      return proprietaMisurataFormat(type, value.value, skipMeasureUnit);
    }
  }, [value, type, proprietaMisurata, skipMeasureUnit, t]);

  const modello = dispositivo?.dispositivoModello;
  const helper = modello?.maschera
    ? getMascheraDispositivoHelper(modello.maschera)
    : null;
  const dynamicLabel =
    proprietaMisurata.type.gruppo === "Fft" &&
    modello?.maschera === "PRAEVIDERE_VIBRATIO_35";
  const { data: registriData } = useGetRegistriDispositivo(dispositivo.id, {
    query: { enabled: dynamicLabel },
  });
  const label = useMemo(() => {
    if (dynamicLabel) {
      if (!registriData || !helper || !('registriToForm' in helper)) {
        return null;
      }

      const values = helper.registriToForm(registriData.data);

      const tag = proprietaMisurata.type.influxDbTag;
      const match = /fft(\d)/.exec(tag);
      if (match) {
        const index = parseInt(match[1]);
        if (index >= 1 && index <= 5) {
          const registro = `fft_f${index}_armonica`;

          const armonica = values[registro];
          const armonicaF = parseFloat(armonica);
          if (armonica) {
            return t("proprietaMisurataLabel.fft.valore", {
              valore: armonicaF ? numeral(armonicaF).format("0.[00]") : armonica,
            });
          } else {
            return t("proprietaMisurataLabel.fft.indice", { indice: index });
          }
        }
      }
    } else {
      return type.labelShort || type.label;
    }
  }, [helper, registriData, proprietaMisurata, dynamicLabel, t]);

  if (!type || !Icon_) {
    return null;
  }

  const perc =
    typeof value?.value === "number"
      ? proprietaMisurataGetPercent(type, value.value)
      : undefined;

  const danger = isDanger(value);
  const oldValue = isOld(value);

  const color: ComponentProps<typeof Typography>["color"] = danger
    ? "error"
    : "success";

  return (
    <Box display="flex" flexDirection="column" alignItems={alignItems} flex={1}>
      <Box display="flex" alignItems="left" mb={0.5} flexDirection="column">
        <Box display="flex" alignItems="center" mb={0.5}>
          <Box
            width="1.25rem"
            height="1.25rem"
            color="grey-400"
            fontSize={size.lg}
            display="flex"
            justifyContent="center"
            alignItems="center"
            mr={1}
            variant="contained"
          >
            <SvgIcon>{Icon_()}</SvgIcon>
          </Box>
          <Typography
            variant={typographyVariantSecondary}
            fontWeight="medium"
            color="text"
          >
            {label}
          </Typography>
        </Box>

        <Tooltip
          title={
            value
              ? t("proprietaMisurataLabel.ultimoRilevamento", {
                  data: moment(value.timestamp).format("DD/MM/YYYY HH:mm:ss"),
                })
              : ""
          }
        >
          <Typography
            variant={typographyVariantPrimary}
            fontWeight="bold"
            color={typeof value?.value === "number" ? color : "warning"}
            sx={{ cursor: "pointer" }}
          >
            {valueText}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default ProprietaMisurataLabel;
